// NavioBuild navigation model — the canonical 5-pillar IA
// (see vault: 04-Strategy/naviobuild/naviobuild-key-areas-canonical.md).
//
// Three layers, mirrored in the sidebar:
//   PILLAR (top-level button)  →  DOMAIN GROUP (sub-header)  →  page (nav item)
//
// Six top-level entries: Today (the engine/lens) + the 5 pillars.
// The Spine and the situational-awareness Engine cut across all pillars; "Today"
// is the engine's home. Domains with no pages yet (Risk, Finance, Crew &
// Mobilization) are intentionally absent until their routes exist.

export type Pillar =
  | 'today'
  | 'project'
  | 'vessel'
  | 'money'
  | 'compliance'
  | 'delivery';

export interface NavItem {
  id: string;
  label: string;
  icon: string; // Lucide icon name (resolved in AppSidebar)
  path: string;
}

/** A domain within a pillar — renders as a sub-header above its pages. */
export interface DomainGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export interface PillarConfig {
  id: Pillar;
  label: string;
  icon: string;
  groups: DomainGroup[];
}

export const pillarOrder: Pillar[] = [
  'today',
  'project',
  'vessel',
  'money',
  'compliance',
  'delivery',
];

export const NAVIGATION_CONFIG: Record<Pillar, PillarConfig> = {
  today: {
    id: 'today',
    label: 'Today',
    icon: 'LayoutDashboard',
    groups: [
      {
        id: 'overview',
        label: 'Today',
        items: [{ id: 'overview', label: 'Overview', icon: 'LayoutDashboard', path: '/' }],
      },
    ],
  },
  project: {
    id: 'project',
    label: 'Project',
    icon: 'Briefcase',
    groups: [
      {
        id: 'command',
        label: 'Command',
        items: [
          { id: 'tracker', label: 'Master Tracker', icon: 'ClipboardList', path: '/tracker' },
        ],
      },
      {
        id: 'setup',
        label: 'Setup',
        items: [
          { id: 'setup', label: 'Setup', icon: 'SlidersHorizontal', path: '/setup' },
          { id: 'lists', label: 'Lists', icon: 'ListChecks', path: '/settings/lists' },
        ],
      },
      {
        id: 'schedule',
        label: 'Schedule',
        items: [{ id: 'schedule', label: 'Schedule', icon: 'ListChecks', path: '/schedule' }],
      },
      {
        id: 'foundation',
        label: 'Foundation',
        items: [{ id: 'foundation', label: 'Foundation', icon: 'Users', path: '/foundation' }],
      },
      {
        id: 'records',
        label: 'Records & Comms',
        items: [{ id: 'records', label: 'Records & Comms', icon: 'Mail', path: '/records' }],
      },
    ],
  },
  vessel: {
    id: 'vessel',
    label: 'Vessel',
    icon: 'Ship',
    groups: [
      {
        id: 'technical',
        label: 'Technical',
        items: [
          { id: 'spine', label: 'Spine', icon: 'FolderTree', path: '/spine' },
          { id: 'drawings', label: 'Drawings', icon: 'PenTool', path: '/drawings' },
          { id: 'tests', label: 'Acceptance Tests', icon: 'CheckCircle', path: '/tests' },
          { id: 'quality', label: 'NCRs & Snags', icon: 'ClipboardCheck', path: '/quality' },
          { id: 'osi', label: 'OSI', icon: 'Package', path: '/osi' },
        ],
      },
      {
        id: 'sitevisits',
        label: 'Site Visits',
        items: [
          { id: 'visits', label: 'Site Visits', icon: 'MapPin', path: '/visits' },
          { id: 'brief', label: 'Site Visit Brief', icon: 'ClipboardList', path: '/brief' },
        ],
      },
    ],
  },
  money: {
    id: 'money',
    label: 'Money',
    icon: 'Banknote',
    groups: [
      {
        id: 'commercial',
        label: 'Commercial',
        items: [
          { id: 'variations', label: 'Variations', icon: 'GitPullRequest', path: '/variations' },
          { id: 'budget', label: 'Budget', icon: 'Calculator', path: '/budget' },
          { id: 'payments', label: 'Payments', icon: 'CreditCard', path: '/payments' },
          { id: 'warranty', label: 'Warranty', icon: 'Wrench', path: '/warranty' },
        ],
      },
      // Finance / Treasury (cash-flow, guarantees, escrow, currency, VAT, insurance)
      // has no pages yet — add a group here when those routes are built.
    ],
  },
  compliance: {
    id: 'compliance',
    label: 'Compliance',
    icon: 'Scale',
    groups: [
      {
        id: 'regulatory',
        label: 'Regulatory',
        items: [
          { id: 'regulatory', label: 'Deliverables', icon: 'Scale', path: '/regulatory' },
          { id: 'ddr', label: 'DDR', icon: 'FileCheck', path: '/ddr' },
        ],
      },
    ],
  },
  delivery: {
    id: 'delivery',
    label: 'Delivery',
    icon: 'Anchor',
    groups: [
      // Crew & Mobilization (delivery-crew plan, training, familiarisation) has no
      // pages yet — add a group here when those routes are built.
      {
        id: 'handover',
        label: 'Handover & Closeout',
        items: [
          { id: 'reports', label: 'Reports', icon: 'BarChart3', path: '/reports' },
          { id: 'handover', label: 'Handover', icon: 'Ship', path: '/handover' },
        ],
      },
    ],
  },
};

/** Which pillar owns the current route (each route belongs to exactly one pillar). */
export function pillarForPath(pathname: string): Pillar {
  for (const pillar of pillarOrder) {
    for (const group of NAVIGATION_CONFIG[pillar].groups) {
      for (const item of group.items) {
        const match =
          item.path === '/'
            ? pathname === '/'
            : pathname === item.path || pathname.startsWith(`${item.path}/`);
        if (match) return pillar;
      }
    }
  }
  return 'today';
}
